import { action, observable } from "mobx";

import apiCaller from "@client/utils/ApiCaller";
import { TokenInfo } from "@typings/Identity";
import { Licensor, LicensorInput } from "@typings/Licensor";
import { LicensingPolicy } from "@typings/LicensingPolicy";

class LicensorStore {
  @observable loggingIn = false;
  @observable loginErrorMessage = "";
  @observable updating = false;
  @observable loading = false;
  @observable licensors: Licensor[] = [];
  @observable tokenInfo: TokenInfo | null = null;
  @observable hasInitialPassword = false;
  @observable licensorId: string | null = null;
  @observable partnerPolicy: LicensingPolicy | null = null;

  @action async load(opts?: {
    forceShowLoading?: boolean;
    hideLoading?: boolean;
  }): Promise<void> {
    this.loading = this.licensors.length === 0;

    if (opts) {
      if (opts.hideLoading) this.loading = false;
      if (opts.forceShowLoading) this.loading = true;
    }
    try {
      const licensors: Licensor[] = await apiCaller.send({ url: "/licensors" });
      this.loadSuccess(licensors);
    } finally {
      this.loadDone();
    }
  }

  @action loadSuccess(licensors: Licensor[]) {
    this.licensors = licensors;
  }

  @action loadDone() {
    this.loading = false;
  }

  @action async login(username: string, password: string): Promise<boolean> {
    this.loggingIn = true;
    this.tokenInfo = null;
    this.loginErrorMessage = "";
    this.hasInitialPassword = false;
    let response;
    try {
      response = await apiCaller.send({
        url: "/login",
        method: "POST",
        data: {
          username,
          password,
        },
        quietError: true,
      });
    } catch (err) {
      this.loginFailed(err.message);
      return false;
    }
    this.loginSuccess(response);
    return true;
  }

  @action loginSuccess(response: {
    hasInitialPassword?: boolean;
    tokenInfo: TokenInfo;
    licensorId?: string;
    licensingPolicy?: LicensingPolicy | null;
  }) {
    this.loggingIn = false;
    this.tokenInfo = response.tokenInfo;
    this.hasInitialPassword = response.hasInitialPassword || false;
    this.licensorId = response.licensorId || null;
    this.partnerPolicy = response.licensingPolicy ?? null;
  }

  @action clearHasInitialPassword() {
    this.hasInitialPassword = false;
  }

  @action loginFailed(message: string) {
    this.loggingIn = false;
    if (message.indexOf("requires authentication") > -1) {
      message = "Incorrect username or password";
    }
    this.loginErrorMessage = message;
  }

  @action async logout() {
    await apiCaller.send({
      url: "/login",
      method: "DELETE",
    });
  }

  async loadTokenInfo() {
    this.loggingIn = true;
    try {
      const response: {
        tokenInfo: TokenInfo;
        licensorId?: string;
        licensingPolicy?: LicensingPolicy | null;
      } = await apiCaller.send({
        url: "/login",
        method: "GET",
        quietError: true,
      });
      this.loginSuccess(response);
    } catch (err) {
      this.loadTokenInfoError();
      throw err;
    }
  }

  @action async refreshPartnerPolicy(): Promise<void> {
    const response: { licensingPolicy?: LicensingPolicy | null } =
      await apiCaller.send({
        url: "/login",
        method: "GET",
        quietError: true,
      });
    this.partnerPolicyUpdated(response.licensingPolicy ?? null);
  }

  @action partnerPolicyUpdated(policy: LicensingPolicy | null) {
    this.partnerPolicy = policy;
  }

  @action loadTokenInfoError() {
    this.loggingIn = false;
  }

  @action async add(licensor: LicensorInput): Promise<Licensor> {
    this.updating = true;
    try {
      const addedLicensor: Licensor = await apiCaller.send({
        url: "/licensors",
        method: "POST",
        data: licensor,
      });
      this.addSuccess(addedLicensor);
      return addedLicensor;
    } finally {
      this.addDone();
    }
  }

  @action addSuccess(licensor: Licensor) {
    const licensors = [...this.licensors, licensor];
    licensors.sort((a, b) => a.name.localeCompare(b.name));
    this.licensors = [...licensors];
  }

  @action addDone() {
    this.updating = false;
  }

  @action async update(
    licensorId: string,
    licensor: LicensorInput,
  ): Promise<Licensor> {
    this.updating = true;
    try {
      const updatedLicensor: Licensor = await apiCaller.send({
        url: `/licensors/${licensorId}`,
        method: "PUT",
        data: licensor,
      });
      this.updateSuccess(updatedLicensor);
      return updatedLicensor;
    } finally {
      this.updateDone();
    }
  }

  @action updateSuccess(licensor: Licensor) {
    const licensors = this.licensors.filter(c => c.id !== licensor.id);
    licensors.push({ ...licensor });
    licensors.sort((a, b) => a.name.localeCompare(b.name));
    this.licensors = [...licensors];
    this.updating = false;
  }

  @action updateDone() {
    this.updating = false;
  }

  @action async updatePassword(password: string): Promise<boolean> {
    this.updating = true;
    const licensorId = this.licensorId;
    try {
      await apiCaller.send({
        url: `/licensors/${licensorId}/password`,
        method: "PUT",
        data: { password },
      });
      return true;
    } catch (err) {
      return false;
    } finally {
      this.updateDone();
    }
  }

  async delete(licensorId: string) {
    await apiCaller.send({
      url: `/licensors/${licensorId}`,
      method: "DELETE",
    });
    this.deleteSuccess(licensorId);
  }

  @action deleteSuccess(licensorId: string) {
    this.licensors = this.licensors.filter(
      licensor => licensor.id !== licensorId,
    );
  }
}

export default new LicensorStore();
